import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useGetFormsBySprintIdQuery,
  usePublishFormMutation,
  useApproveFormMutation,
  useRejectFormMutation,
  useRestartFormMutation,
  useGetBacklinksQuery,
} from '../store/topicApi';
import ConfirmModal from './ConfirmModal';
import BacklinksModal from './BacklinksModal';
import DataTable from './DataTable';
import Pagination from './Pagination';

const ITEMS_PER_PAGE = 10;

const approvalBadge = (form: any) => {
  if (!form.submitted) return <span className="badge-gray">—</span>;
  if (form.userApprovalStatus === 'approved') return <span className="badge-green">✓ Approved</span>;
  if (form.userApprovalStatus === 'rejected') return <span className="badge-red">✗ Rejected</span>;
  return <span className="badge-yellow">⏳ Pending</span>;
};

const SprintForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: forms = [], isLoading } = useGetFormsBySprintIdQuery(id || '');
  const [publishForm] = usePublishFormMutation();
  const [approveForm] = useApproveFormMutation();
  const [rejectForm] = useRejectFormMutation();
  const [restartForm] = useRestartFormMutation();

  const [processingFormId, setProcessingFormId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [restartModalOpen, setRestartModalOpen] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);

  const [isBacklinksModalOpen, setIsBacklinksModalOpen] = useState(false);
  const [backlinksFormId, setBacklinksFormId] = useState<string | null>(null);
  const [backlinksFormTitle, setBacklinksFormTitle] = useState<string>('');

  const { data: backlinksData, isLoading: backlinksLoading, error: backlinksError } = useGetBacklinksQuery(
    backlinksFormId || '',
    { skip: !backlinksFormId }
  );

  const handleViewBacklinks = (formId: string, title: string, published: boolean) => {
    if (!published) {
      toast.error('This blog must be published before generating backlinks.');
      return;
    }
    setBacklinksFormId(formId);
    setBacklinksFormTitle(title);
    setIsBacklinksModalOpen(true);
  };

  const handleApprove = (formId: string) => { setSelectedFormId(formId); setApproveModalOpen(true); };
  const handleReject = (formId: string) => { setSelectedFormId(formId); setRejectModalOpen(true); };
  const handleRestart = (formId: string) => { setSelectedFormId(formId); setRestartModalOpen(true); };

  const confirmApprove = async (comments?: string) => {
    if (!selectedFormId) return;
    setProcessingFormId(selectedFormId);
    try {
      await approveForm(selectedFormId).unwrap();
      await publishForm({ formId: selectedFormId, comments }).unwrap();
      toast.success('Blog approved and published successfully!');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to approve/publish');
    } finally {
      setProcessingFormId(null);
      setSelectedFormId(null);
    }
  };

  const confirmReject = async () => {
    if (!selectedFormId) return;
    setProcessingFormId(selectedFormId);
    try {
      await rejectForm(selectedFormId).unwrap();
      toast.success('Blog rejected.');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to reject');
    } finally { setProcessingFormId(null); setSelectedFormId(null); }
  };

  const confirmRestart = async () => {
    if (!selectedFormId) return;
    setProcessingFormId(selectedFormId);
    try {
      await restartForm(selectedFormId).unwrap();
      toast.success('Workflow restarted.');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to restart');
    } finally { setProcessingFormId(null); setSelectedFormId(null); }
  };

  const totalPages = Math.ceil(forms.length / ITEMS_PER_PAGE);
  const paginated = forms.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <>
      <div>
        <div className="mb-5">
          <h2 className="font-display font-bold text-xl text-trident-gray-900">Forms in this Sprint</h2>
          <p className="text-sm text-trident-gray-500">Total: {forms.length} blog topics</p>
        </div>

        <DataTable
          loading={isLoading}
          rows={paginated}
          rowKey={(f) => f._id}
          emptyMessage="No forms yet for this sprint"
          columns={[
            {
              key: 'sno', label: '#', width: '60px', align: 'center',
              render: (_r, i) => <span className="text-trident-gray-400 font-mono text-xs">{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</span>,
            },
            {
              key: 'title', label: 'Title',
              render: (f: any) => (
                <div>
                  <div className="font-semibold text-trident-gray-900">{f.title}</div>
                  {f.userEdited && (
                    <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200">
                      ✏️ Edited by user
                    </span>
                  )}
                </div>
              ),
            },
            {
              key: 'assignee', label: 'Assignee',
              render: (f: any) => <span className="text-sm text-trident-gray-600">{f.assignee || 'N/A'}</span>,
            },
            {
              key: 'submitted', label: 'Status', align: 'center',
              render: (f: any) => f.submitted
                ? <span className="badge-green">✓ Submitted</span>
                : <span className="badge-gray">Pending</span>,
            },
            {
              key: 'approval', label: 'User Approval',
              render: (f: any) => (
                <div>
                  {approvalBadge(f)}
                  {f.tempPublishedUrl && !f.published && (
                    <a
                      href={f.tempPublishedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-xs text-trident-orange hover:underline mt-1 font-semibold"
                    >
                      View Preview →
                    </a>
                  )}
                </div>
              ),
            },
            {
              key: 'backlinks', label: 'Backlinks', align: 'center',
              render: (f: any) => f.published ? (
                <button
                  onClick={() => handleViewBacklinks(f._id, f.title, f.published)}
                  className="text-xs font-display font-semibold text-trident-orange hover:text-trident-orange-dark hover:underline"
                >
                  View
                </button>
              ) : (
                <span className="text-xs text-trident-gray-400">Not Published</span>
              ),
            },
            {
              key: 'actions', label: 'Actions', align: 'right',
              render: (f: any) => {
                const canAct = f.submitted && f.userApprovalStatus === 'approved'
                  && f.adminApprovalStatus !== 'approved' && !f.published;
                if (f.published) {
                  return (
                    <a
                      href={f.publishedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-display font-semibold text-green-600 hover:text-green-700"
                    >
                      ✓ Live
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    </a>
                  );
                }
                return (
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => handleApprove(f._id)}
                      disabled={!canAct || processingFormId === f._id}
                      title="Approve & Publish"
                      className={`p-2 rounded-lg transition-all ${canAct ? 'text-green-600 hover:bg-green-50' : 'text-trident-gray-300 cursor-not-allowed'}`}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </button>
                    <button
                      onClick={() => handleReject(f._id)}
                      disabled={!canAct || processingFormId === f._id}
                      title="Reject"
                      className={`p-2 rounded-lg transition-all ${canAct ? 'text-red-500 hover:bg-red-50' : 'text-trident-gray-300 cursor-not-allowed'}`}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                    <button
                      onClick={() => handleRestart(f._id)}
                      disabled={!canAct || processingFormId === f._id}
                      title="Restart Workflow"
                      className={`p-2 rounded-lg transition-all ${canAct ? 'text-trident-orange hover:bg-trident-orange-light' : 'text-trident-gray-300 cursor-not-allowed'}`}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                    </button>
                  </div>
                );
              },
            },
          ]}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={forms.length}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>

      <ConfirmModal
        isOpen={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        onConfirm={confirmApprove}
        title="Approve & Publish Blog"
        message="Approve and publish this blog to the ITTrident website? You can optionally add revision comments to refine the content before publishing."
        confirmText="Approve & Publish"
        confirmButtonColor="bg-green-600 hover:bg-green-700"
        showComments={true}
      />
      <ConfirmModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={confirmReject}
        title="Reject Blog"
        message="Are you sure you want to reject this blog?"
        confirmText="Reject"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
      />
      <ConfirmModal
        isOpen={restartModalOpen}
        onClose={() => setRestartModalOpen(false)}
        onConfirm={confirmRestart}
        title="Restart Workflow"
        message="Restart this blog workflow? This will delete the submission and reset the form."
        confirmText="Restart"
        confirmButtonColor="bg-trident-orange hover:bg-trident-orange-dark"
      />

      <BacklinksModal
        isOpen={isBacklinksModalOpen}
        onClose={() => { setIsBacklinksModalOpen(false); setBacklinksFormId(null); setBacklinksFormTitle(''); }}
        backlinks={backlinksData?.backlinks || []}
        loading={backlinksLoading}
        error={backlinksError ? 'Failed to load backlinks. Please try again.' : undefined}
        title={backlinksFormTitle}
      />
    </>
  );
};

export default SprintForm;
